import clsx from "clsx";
import React, { FC, ForwardedRef, InputHTMLAttributes } from "react";
import styled from "styled-components";

import { FormErrorModel, IStyledAttributes } from "@ryde/shared";
import { deepPurpleMui, greyMui, redMui, rydeColors } from "@ryde/theme";

interface IInput extends InputHTMLAttributes<HTMLInputElement> {
  error?: FormErrorModel;
}

const InputOrdinaryStyled = styled.input.attrs<IStyledAttributes>({
  "data-e2e": "input-ordinary",
})<IInput>`
  color: ${props => props.color || rydeColors.greyFocusedInput};
  display: block;
  width: ${props => props.width || "100%"};
  font-family: inherit;
  padding: 0.375rem 0.75rem;
  font-size: 14px;
  font-weight: 400;
  line-height: 32px;
  background-color: ${rydeColors.white};
  background-clip: padding-box;
  border: 1px solid ${greyMui._300};
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border-radius: 8px;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
  &:focus {
    background-color: ${rydeColors.white};
    border-color: ${rydeColors.lightBlue};
    outline: 0;
    box-shadow: 0 0 0 0.25rem rgb(13 110 253 / 25%);
  }
  &:hover:not([disabled], .error) {
    border-color: ${deepPurpleMui._300};
  }
  &:disabled {
    background: ${greyMui._300};
  }
  &::placeholder {
    color: ${rydeColors.grey};
  }
  &.error {
    border-color: ${redMui._700};
  }
`;

const InputStyledLarge = styled(InputOrdinaryStyled)`
  min-height: calc(1.5em + 1rem + 2px);
  padding: 0.5rem 1rem;
  font-size: 1.25rem;
  border-radius: 0.5rem;
`;

const InputErrorStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "input-ordinary-error",
})`
  color: #fe0000;
  margin-inline-start: 6px;
`;

export const Input: FC<IInput> = React.forwardRef(
  (props, ref: ForwardedRef<HTMLInputElement>) => {
    return (
      <>
        <InputOrdinaryStyled
          {...props}
          className={clsx({ error: props.error }, props.className)}
          ref={ref}
        />
        {!!props.error && (
          <InputErrorStyled>{props.error as string}</InputErrorStyled>
        )}
      </>
    );
  },
);

export const InputPassword: FC<IInput> = React.forwardRef(
  (props, ref: ForwardedRef<HTMLInputElement>) => {
    const updatedProps = { ...props, type: "password" };
    return (
      <>
        <InputOrdinaryStyled
          {...updatedProps}
          className={clsx({ error: props.error }, props.className)}
          ref={ref}
        />
        {!!props.error && (
          <InputErrorStyled>{props.error as string}</InputErrorStyled>
        )}
      </>
    );
  },
);

export const InputNumeric: FC<IInput> = React.forwardRef(
  (props, ref: ForwardedRef<HTMLInputElement>) => {
    const updatedProps = { ...props, type: "number" };
    return (
      <>
        <InputOrdinaryStyled
          {...updatedProps}
          className={clsx({ error: props.error }, props.className)}
          ref={ref}
        />
        {!!props.error && (
          <InputErrorStyled>{props.error as string}</InputErrorStyled>
        )}
      </>
    );
  },
);

export const InputDate: FC<IInput> = React.forwardRef(
  (props, ref: ForwardedRef<HTMLInputElement>) => {
    const updatedProps = { ...props, type: "date" };
    return (
      <>
        <InputOrdinaryStyled
          {...updatedProps}
          className={clsx({ error: props.error }, props.className)}
          ref={ref}
        />
        {!!props.error && (
          <InputErrorStyled>{props.error as string}</InputErrorStyled>
        )}
      </>
    );
  },
);

export const InputTime: FC<IInput> = React.forwardRef(
  (props, ref: ForwardedRef<HTMLInputElement>) => {
    const updatedProps = { ...props, type: "time" };
    return (
      <>
        <InputOrdinaryStyled
          {...updatedProps}
          className={clsx({ error: props.error }, props.className)}
          ref={ref}
        />
        {!!props.error && (
          <InputErrorStyled>{props.error as string}</InputErrorStyled>
        )}
      </>
    );
  },
);

export const InputDateTime: FC<IInput> = React.forwardRef(
  (props, ref: ForwardedRef<HTMLInputElement>) => {
    const updatedProps = { ...props, type: "datetime-local" };
    return (
      <>
        <InputOrdinaryStyled
          {...updatedProps}
          className={clsx({ error: props.error }, props.className)}
          ref={ref}
        />
        {!!props.error && (
          <InputErrorStyled>{props.error as string}</InputErrorStyled>
        )}
      </>
    );
  },
);

export const InputLarge: FC<IInput> = React.forwardRef(
  (props, ref: ForwardedRef<HTMLInputElement>) => {
    return (
      <>
        <InputStyledLarge
          {...props}
          className={clsx({ error: props.error }, props.className)}
          ref={ref}
        />
        {!!props.error && (
          <InputErrorStyled>{props.error as string}</InputErrorStyled>
        )}
      </>
    );
  },
);
